/**
 * GS1 128 Barcode Parser
 * Parses GS1 128 barcodes and extracts AI (Application Identifier) data
 * Supports configurable separators for variable-length fields
 */

export interface GS1Data {
  sscc?: string;        // AI 00 - Serial Shipping Container Code (SSCC)
  gtin?: string;        // AI 01, 02
  batchNo?: string;     // AI 10  
  expiryDate?: string;  // AI 15, 17
  quantity?: number;    // AI 37
  serialNo?: string;    // AI 21
  bestBeforeDate?: string; // AI 15
  useByDate?: string;   // AI 17
  variantNumber?: string; // AI 20
  netWeight?: number;   // AI 310n
  grossWeight?: number; // AI 330n
  price?: number;       // AI 392n
  pricePerUnit?: number; // AI 394n
  couponValue?: number; // AI 8110
  internalInfo?: string; // AI 90-99 (Company internal information)
  raw?: string;         // Original barcode
  parsedAIs?: { [key: string]: string }; // All parsed AIs
}

export interface AIDefinition {
  ai: string;
  length: number; // -1 for variable length
  field: keyof GS1Data;
  description: string;
  dataType: 'string' | 'number' | 'date';
}

// GS1 AI definitions - most common ones
export const AI_DEFINITIONS: AIDefinition[] = [
  { ai: '00', length: 18, field: 'sscc', description: 'Serial Shipping Container Code (SSCC)', dataType: 'string' },
  { ai: '01', length: 14, field: 'gtin', description: 'Global Trade Item Number (GTIN)', dataType: 'string' },
  { ai: '02', length: 14, field: 'gtin', description: 'GTIN of contained trade items', dataType: 'string' },
  { ai: '10', length: -1, field: 'batchNo', description: 'Batch/Lot number', dataType: 'string' },
  { ai: '15', length: 6, field: 'bestBeforeDate', description: 'Best before date (YYMMDD)', dataType: 'date' },
  { ai: '17', length: 6, field: 'useByDate', description: 'Use by date (YYMMDD)', dataType: 'date' },
  { ai: '20', length: 2, field: 'variantNumber', description: 'Variant number', dataType: 'string' },
  { ai: '21', length: -1, field: 'serialNo', description: 'Serial number', dataType: 'string' },
  { ai: '37', length: -1, field: 'quantity', description: 'Number of units contained', dataType: 'number' },
  { ai: '3100', length: 6, field: 'netWeight', description: 'Net weight (kg)', dataType: 'number' },
  { ai: '3101', length: 6, field: 'netWeight', description: 'Net weight (kg)', dataType: 'number' },
  { ai: '3102', length: 6, field: 'netWeight', description: 'Net weight (kg)', dataType: 'number' },
  { ai: '3103', length: 6, field: 'netWeight', description: 'Net weight (kg)', dataType: 'number' },
  { ai: '3300', length: 6, field: 'grossWeight', description: 'Gross weight (kg)', dataType: 'number' },
  { ai: '3301', length: 6, field: 'grossWeight', description: 'Gross weight (kg)', dataType: 'number' },
  { ai: '3302', length: 6, field: 'grossWeight', description: 'Gross weight (kg)', dataType: 'number' },
  { ai: '3303', length: 6, field: 'grossWeight', description: 'Gross weight (kg)', dataType: 'number' },
  { ai: '3920', length: -1, field: 'price', description: 'Price', dataType: 'number' },
  { ai: '3921', length: -1, field: 'price', description: 'Price', dataType: 'number' },
  { ai: '3922', length: -1, field: 'price', description: 'Price', dataType: 'number' },
  { ai: '3923', length: -1, field: 'price', description: 'Price', dataType: 'number' },
  { ai: '3940', length: -1, field: 'pricePerUnit', description: 'Price per unit', dataType: 'number' },
  { ai: '3941', length: -1, field: 'pricePerUnit', description: 'Price per unit', dataType: 'number' },
  { ai: '3942', length: -1, field: 'pricePerUnit', description: 'Price per unit', dataType: 'number' },
  { ai: '3943', length: -1, field: 'pricePerUnit', description: 'Price per unit', dataType: 'number' },
  { ai: '8110', length: -1, field: 'couponValue', description: 'Coupon extended code', dataType: 'string' },
  { ai: '90', length: -1, field: 'internalInfo', description: 'Mutually agreed between trading partners', dataType: 'string' },
  { ai: '91', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '92', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '93', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '94', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '95', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '96', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '97', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '98', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' },
  { ai: '99', length: -1, field: 'internalInfo', description: 'Company internal information', dataType: 'string' }
];

// Default separators: FNC1 (ASCII 29), Tilde (~), and Section sign (§)
const DEFAULT_SEPARATORS = String.fromCharCode(29) + '~§';

/**
 * Parse GS1 128 barcode with configurable separators
 * @param barcode Raw barcode string
 * @param separators String containing separator characters (default: FNC1 + tilde + section sign)
 * @returns Parsed GS1 data
 */
export function parseGS1Barcode(barcode: string, separators: string = DEFAULT_SEPARATORS): GS1Data {
  if (!barcode) {
    return { raw: barcode };
  }

  const gs1Data: GS1Data = {
    raw: barcode,
    parsedAIs: {}
  };

  // Create a single separator character to use internally (use first separator from string)
  const internalSeparator = String.fromCharCode(29); // Use FNC1 as internal representation

  // Replace all configured separators with internal separator for consistent parsing
  let cleanBarcode = barcode;
  for (let i = 0; i < separators.length; i++) {
    const sep = separators[i];
    const escapedSep = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleanBarcode = cleanBarcode.replace(new RegExp(escapedSep, 'g'), internalSeparator);
  }

  // Clean barcode - keep only alphanumeric and internal separator
  const separatorEscaped = internalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  cleanBarcode = cleanBarcode.replace(new RegExp(`[^\\w\\d${separatorEscaped}]`, 'g'), '');

  // Remove trailing separators (common when scanners automatically append separator at end)
  while (cleanBarcode.endsWith(internalSeparator)) {
    cleanBarcode = cleanBarcode.slice(0, -1);
  }

  // Handle different input formats
  if (cleanBarcode.startsWith(']C1')) {
    // Code 128 format indicator
    cleanBarcode = cleanBarcode.substring(3);
  }

  let position = 0;
  let attempts = 0;
  const maxAttempts = 50; // Prevent infinite loops

  while (position < cleanBarcode.length && attempts < maxAttempts) {
    attempts++;
    
    // Try to find AI starting at current position
    let aiFound = false;
    
    // Sort AI definitions by length (longest first) to avoid partial matches
    const sortedAIs = [...AI_DEFINITIONS].sort((a, b) => b.ai.length - a.ai.length);
    
    for (const aiDef of sortedAIs) {
      const aiCode = aiDef.ai;
      
      if (cleanBarcode.substring(position, position + aiCode.length) === aiCode) {
        position += aiCode.length;
        
        let value: string;
        
        if (aiDef.length === -1) {
          // Variable length - PRIORITIZE separator first
          const nextSeparatorPosition = cleanBarcode.indexOf(internalSeparator, position);
          
          if (nextSeparatorPosition !== -1 && nextSeparatorPosition > position) {
            // Separator found - use it as the end position
            value = cleanBarcode.substring(position, nextSeparatorPosition);
            position = nextSeparatorPosition + 1; // Skip the separator
          } else {
            // No separator - look for next AI or end of string
            const nextAiPosition = findNextAI(cleanBarcode, position);
            
            if (nextAiPosition !== -1 && nextAiPosition > position) {
              value = cleanBarcode.substring(position, nextAiPosition);
              position = nextAiPosition;
            } else {
              // No next AI - take rest of string
              value = cleanBarcode.substring(position);
              position = cleanBarcode.length;
            }
          }
        } else {
          // Fixed length
          const expectedEnd = position + aiDef.length;

          // Special handling for GTIN (AI 01/02): Can be 13 or 14 digits
          // Try to determine correct length by checking for next AI
          if ((aiCode === '01' || aiCode === '02') && aiDef.length === 14) {
            let actualGtinLength = 14;

            // Check if there's a known AI at position 13 or 14
            // If there's a valid AI at position 13 (after 13-digit GTIN), use 13 digits
            if (position + 13 < cleanBarcode.length) {
              const potential2CharAI = cleanBarcode.substring(position + 13, position + 15);
              const potential4CharAI = cleanBarcode.substring(position + 13, position + 17);

              // Check if this matches a known AI
              const matchesAIat13 = sortedAIs.some(ai =>
                (ai.ai.length === 2 && ai.ai === potential2CharAI) ||
                (ai.ai.length === 4 && ai.ai === potential4CharAI)
              );

              if (matchesAIat13) {
                // Validate both 13-digit and 14-digit GTIN with checksum
                const gtin13 = cleanBarcode.substring(position, position + 13);
                const gtin14 = cleanBarcode.substring(position, position + 14);

                const gtin13Valid = validateGTIN(gtin13);
                const gtin14Valid = validateGTIN(gtin14);

                console.log(`🔍 GTIN validation: 13-digit (${gtin13}): ${gtin13Valid}, 14-digit (${gtin14}): ${gtin14Valid}`);

                // Prefer 13-digit if valid and next position is a known AI
                if (gtin13Valid) {
                  actualGtinLength = 13;
                  console.log(`✂️ Using 13-digit GTIN: ${gtin13}`);
                } else if (gtin14Valid) {
                  actualGtinLength = 14;
                  console.log(`✅ Using 14-digit GTIN: ${gtin14}`);
                } else {
                  // Neither validates - default to 14 digits
                  console.warn(`⚠️ Neither GTIN length validates, using 14 digits`);
                  actualGtinLength = 14;
                }
              } else {
                // No AI at position 13, use full 14 digits
                actualGtinLength = 14;
              }
            }

            value = cleanBarcode.substring(position, position + actualGtinLength);
            position += actualGtinLength;
          } else {
            // Normal fixed length
            value = cleanBarcode.substring(position, expectedEnd);
            position = expectedEnd;
          }
        }

        // Store raw AI value
        gs1Data.parsedAIs![aiCode] = value;
        
        // Convert and store in appropriate field
        try {
          if (aiDef.dataType === 'number') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              (gs1Data as any)[aiDef.field] = numValue;
            }
          } else if (aiDef.dataType === 'date') {
            const formattedDate = formatGS1Date(value);
            if (formattedDate) {
              (gs1Data as any)[aiDef.field] = formattedDate;
              // Also set generic expiryDate for backward compatibility
              if (aiDef.ai === '15' || aiDef.ai === '17') {
                gs1Data.expiryDate = formattedDate;
              }
            }
          } else {
            // String
            (gs1Data as any)[aiDef.field] = value;
          }
        } catch (error) {
          console.warn(`Error parsing AI ${aiCode} value ${value}:`, error);
        }
        
        aiFound = true;
        break;
      }
    }
    
    if (!aiFound) {
      position++;
    }
  }
  
  return gs1Data;
}

/**
 * Find the next AI position in the barcode
 * Only returns a position if it's likely a real AI (not part of data)
 * @param barcode Barcode string
 * @param startPosition Start position
 * @returns Position of next AI or -1 if not found
 */
function findNextAI(barcode: string, startPosition: number): number {
  // Look for potential AIs, but be careful not to find "fake" AIs in data
  // Strategy: AIs are typically 2-4 digits, so we look for patterns
  const sortedAIs = [...AI_DEFINITIONS].sort((a, b) => b.ai.length - a.ai.length);
  
  for (let i = startPosition; i < barcode.length; i++) {
    // Check if any AI matches at this position
    for (const aiDef of sortedAIs) {
      if (i + aiDef.ai.length <= barcode.length) {
        const potential = barcode.substring(i, i + aiDef.ai.length);
        if (potential === aiDef.ai) {
          // Found a potential AI
          // Additional validation: make sure we have enough characters left for the AI data
          const remainingChars = barcode.length - (i + aiDef.ai.length);
          
          if (aiDef.length === -1) {
            // Variable length - need at least 1 character
            if (remainingChars >= 1) {
              return i;
            }
          } else {
            // Fixed length - need exact length
            if (remainingChars >= aiDef.length) {
              return i;
            }
          }
        }
      }
    }
  }
  
  return -1;
}

/**
 * Format GS1 date (YYMMDD) to readable format
 * @param dateStr Date string in YYMMDD format
 * @returns Formatted date string or null if invalid
 */
function formatGS1Date(dateStr: string): string | null {
  if (!dateStr || dateStr.length !== 6) {
    return null;
  }
  
  try {
    const year = parseInt(dateStr.substring(0, 2));
    const month = parseInt(dateStr.substring(2, 4));
    const day = parseInt(dateStr.substring(4, 6));
    
    // GS1 General Specifications Section 7.12: 00-49 → 20xx, 50-99 → 19xx
    // TODO: Vurder glidende vindu basert på gjeldende år for mer robust håndtering
    const fullYear = year <= 49 ? 2000 + year : 1900 + year;
    
    const date = new Date(Date.UTC(fullYear, month - 1, day));
    
    // Validate date
    if (date.getFullYear() !== fullYear || 
        date.getMonth() !== month - 1 || 
        date.getDate() !== day) {
      return null;
    }
    
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  } catch (error) {
    return null;
  }
}

/**
 * Validate GTIN checksum (fixed to calculate from right to left)
 * @param gtin GTIN string
 * @returns True if valid checksum
 */
export function validateGTIN(gtin: string): boolean {
  if (!gtin || gtin.length < 8 || gtin.length > 14) {
    return false;
  }
  
  // Pad to 14 digits
  const paddedGtin = gtin.padStart(14, '0');
  
  // Calculate checksum from RIGHT to LEFT
  // Starting from the right (excluding check digit), alternate 3 and 1
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const digit = parseInt(paddedGtin[i]);
    // Position from right (0 = leftmost, 12 = rightmost before check digit)
    const positionFromRight = 12 - i;
    // From right: position 0 (rightmost before check) gets weight 3, then 1, 3, 1, ...
    const weight = positionFromRight % 2 === 0 ? 3 : 1;
    sum += digit * weight;
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(paddedGtin[13]);
}

/**
 * Get all AI definitions for reference
 * @returns Array of AI definitions
 */
export function getAIDefinitions(): AIDefinition[] {
  return AI_DEFINITIONS;
}

/**
 * Format parsed GS1 data for display
 * @param gs1Data Parsed GS1 data
 * @returns Formatted string
 */
export function formatGS1DataForDisplay(gs1Data: GS1Data): string {
  const parts: string[] = [];
  
  if (gs1Data.sscc) parts.push(`SSCC: ${gs1Data.sscc}`);
  if (gs1Data.gtin) parts.push(`GTIN: ${gs1Data.gtin}`);
  if (gs1Data.batchNo) parts.push(`Batch: ${gs1Data.batchNo}`);
  if (gs1Data.serialNo) parts.push(`Serial: ${gs1Data.serialNo}`);
  if (gs1Data.quantity) parts.push(`Quantity: ${gs1Data.quantity}`);
  if (gs1Data.bestBeforeDate) parts.push(`Best before: ${gs1Data.bestBeforeDate}`);
  if (gs1Data.useByDate) parts.push(`Use by: ${gs1Data.useByDate}`);
  if (gs1Data.expiryDate) parts.push(`Expiry: ${gs1Data.expiryDate}`);
  if (gs1Data.netWeight) parts.push(`Net weight: ${gs1Data.netWeight} kg`);
  if (gs1Data.grossWeight) parts.push(`Gross weight: ${gs1Data.grossWeight} kg`);
  if (gs1Data.price) parts.push(`Price: ${gs1Data.price}`);
  if (gs1Data.internalInfo) parts.push(`Internal: ${gs1Data.internalInfo}`);
  
  return parts.join(', ');
}

/**
 * Get AI definition by AI code
 * @param ai AI code
 * @returns AI definition or undefined if not found
 */
export function getAIDefinition(ai: string): AIDefinition | undefined {
  return AI_DEFINITIONS.find(def => def.ai === ai);
}

/**
 * Parse barcode and return visual breakdown for display
 * @param barcode Raw barcode string
 * @param separators String containing separator characters (default: FNC1 + tilde)
 * @returns Array of parsed segments with AI info
 */
export interface BarcodeSegment {
  ai: string;
  value: string;
  description: string;
  dataType: string;
  isValid: boolean;
}

export function parseGS1BarcodeWithSegments(barcode: string, separators: string = DEFAULT_SEPARATORS): BarcodeSegment[] {
  const segments: BarcodeSegment[] = [];

  if (!barcode) {
    return segments;
  }

  // Create a single separator character to use internally
  const internalSeparator = String.fromCharCode(29);

  // Replace all configured separators with internal separator for consistent parsing
  let cleanBarcode = barcode;
  for (let i = 0; i < separators.length; i++) {
    const sep = separators[i];
    const escapedSep = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleanBarcode = cleanBarcode.replace(new RegExp(escapedSep, 'g'), internalSeparator);
  }

  // Clean barcode
  const separatorEscaped = internalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  cleanBarcode = cleanBarcode.replace(new RegExp(`[^\\w\\d${separatorEscaped}]`, 'g'), '');

  // Remove trailing separators (common when scanners automatically append separator at end)
  while (cleanBarcode.endsWith(internalSeparator)) {
    cleanBarcode = cleanBarcode.slice(0, -1);
  }

  if (cleanBarcode.startsWith(']C1')) {
    cleanBarcode = cleanBarcode.substring(3);
  }

  let position = 0;
  let attempts = 0;
  const maxAttempts = 50;

  while (position < cleanBarcode.length && attempts < maxAttempts) {
    attempts++;
    let aiFound = false;
    
    const sortedAIs = [...AI_DEFINITIONS].sort((a, b) => b.ai.length - a.ai.length);
    
    for (const aiDef of sortedAIs) {
      const aiCode = aiDef.ai;
      
      if (cleanBarcode.substring(position, position + aiCode.length) === aiCode) {
        position += aiCode.length;
        
        let value: string;
        
        if (aiDef.length === -1) {
          // Variable length - PRIORITIZE separator first
          const nextSeparatorPosition = cleanBarcode.indexOf(internalSeparator, position);
          
          if (nextSeparatorPosition !== -1 && nextSeparatorPosition > position) {
            // Separator found - use it as the end position
            value = cleanBarcode.substring(position, nextSeparatorPosition);
            position = nextSeparatorPosition + 1; // Skip the separator
          } else {
            // No separator - look for next AI or end of string
            const nextAiPosition = findNextAI(cleanBarcode, position);
            
            if (nextAiPosition !== -1 && nextAiPosition > position) {
              value = cleanBarcode.substring(position, nextAiPosition);
              position = nextAiPosition;
            } else {
              // No next AI - take rest of string
              value = cleanBarcode.substring(position);
              position = cleanBarcode.length;
            }
          }
        } else {
          value = cleanBarcode.substring(position, position + aiDef.length);
          position += aiDef.length;
        }
        
        // Validate based on type
        let isValid = true;
        if (aiDef.ai === '01' || aiDef.ai === '02') {
          isValid = validateGTIN(value);
        }
        
        segments.push({
          ai: aiCode,
          value: value,
          description: aiDef.description,
          dataType: aiDef.dataType,
          isValid: isValid
        });
        
        aiFound = true;
        break;
      }
    }
    
    if (!aiFound) {
      position++;
    }
  }
  
  return segments;
}

/**
 * Get the default separators string
 * @returns Default separators (FNC1 + tilde + section sign)
 */
export function getDefaultSeparators(): string {
  return DEFAULT_SEPARATORS;
}
